import React, { useState } from 'react';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
} from '../components/legacy-ball/table-management/designTokens';

/* ─── QR Code Entry Type ─── */

interface QRCodeEntry {
  id: string;
  label: string;
  path: string;
  description: string;
  category: 'guest-services' | 'event-info' | 'delegate-tools' | 'public';
  icon: string;
}

/* ─── Static QR Code Directory Data ─── */

const QR_CODES: QRCodeEntry[] = [
  // Guest Services
  {
    id: 'login',
    label: 'Guest Login',
    path: '/login',
    description: 'Guests scan to log in with their ticket number (TKT-XXX) and access their My Weekend page.',
    category: 'guest-services',
    icon: '🔐',
  },
  {
    id: 'my-info',
    label: 'My Info Lookup',
    path: '/my-info',
    description: 'Guests look up their seating, table assignment, and QR guest card by ticket or order number.',
    category: 'guest-services',
    icon: '🪪',
  },
  {
    id: 'guest-directory',
    label: 'Guest Directory',
    path: '/guest-directory',
    description: 'Public guest directory — guests can search for other attendees and their seating info.',
    category: 'guest-services',
    icon: '📒',
  },
  {
    id: 'guest-questions',
    label: 'Guest Questions',
    path: '/guest-questions',
    description: 'Guests can submit questions to the organizing team and browse FAQs.',
    category: 'guest-services',
    icon: '❓',
  },
  {
    id: 'story-builder',
    label: 'Story Builder',
    path: '/story-builder/interview',
    description: 'Guests record their personal KDU story through a guided interview for the weekend booklet.',
    category: 'guest-services',
    icon: '📖',
  },

  // Event Info
  {
    id: 'reunion-dashboard',
    label: 'Reunion Weekend Dashboard',
    path: '/dashboard/reunion-weekend',
    description: 'Public dashboard with live stats — check-in count, event progress, and countdown.',
    category: 'event-info',
    icon: '📊',
  },
  {
    id: 'events',
    label: 'Events & Schedule',
    path: '/events',
    description: 'Full schedule of all reunion weekend events with times, venues, and details.',
    category: 'event-info',
    icon: '📅',
  },
  {
    id: 'vendor-showcase',
    label: 'Vendor Showcase',
    path: '/vendor-showcase',
    description: 'Browse registered vendors, their booths, and product offerings.',
    category: 'event-info',
    icon: '🏪',
  },
  {
    id: 'vendor-map',
    label: 'Vendor Map',
    path: '/vendor-map',
    description: 'Interactive map showing vendor booth locations across event zones.',
    category: 'event-info',
    icon: '🗺️',
  },
  {
    id: 'food-catalog',
    label: 'Food Catalog',
    path: '/food-catalog',
    description: 'Browse the food menu for the Awujoh and other catered events.',
    category: 'event-info',
    icon: '🍽️',
  },
  {
    id: 'marketplace',
    label: 'KDU GA Marketplace',
    path: '/marketplace',
    description: 'Browse and purchase reunion merchandise, memorabilia, and KDU GA money.',
    category: 'event-info',
    icon: '🛍️',
  },
  {
    id: 'nearby',
    label: 'Nearby Places',
    path: '/nearby',
    description: 'Restaurants, hotels, pharmacies, and services near the reunion venues.',
    category: 'event-info',
    icon: '📍',
  },

  // Delegate Tools
  {
    id: 'packet-pickup',
    label: 'Packet Pickup / Check-In',
    path: '/packet-pickup',
    description: 'Delegates scan to open the Packet Pickup QR scanner for guest check-in.',
    category: 'delegate-tools',
    icon: '📦',
  },
  {
    id: 'kongosa-box',
    label: 'Kongosa Box',
    path: '/kongosa-box',
    description: 'Live event feed for delegates — alerts, updates, and real-time coordination.',
    category: 'delegate-tools',
    icon: '📡',
  },
  {
    id: 'gx-console',
    label: 'GX Team Console',
    path: '/gx-console',
    description: 'Guest Experience team console for task management and live event coordination.',
    category: 'delegate-tools',
    icon: '🎯',
  },

  // Public (no auth)
  {
    id: 'krio-fun-facts',
    label: 'Krio Fun Facts',
    path: '/krio-fun-facts',
    description: 'Interactive Krio language learning — proverbs, sayings, and translations.',
    category: 'public',
    icon: '🎲',
  },
  {
    id: 'privacy',
    label: 'Privacy Policy',
    path: '/privacy',
    description: 'KDU Hub privacy policy and data handling information.',
    category: 'public',
    icon: '🔒',
  },
];

const CATEGORIES = [
  { key: 'guest-services' as const, label: 'Guest Services', icon: '🧳' },
  { key: 'event-info' as const, label: 'Event Information', icon: '📅' },
  { key: 'delegate-tools' as const, label: 'Delegate Tools', icon: '⚡' },
  { key: 'public' as const, label: 'Public Pages', icon: '🌐' },
];

/* ─── QR Code Generation ─── */

function getQRImageUrl(fullUrl: string, size = 200): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(fullUrl)}`;
}

// Download a QR as a PNG. Fetches the image as a blob (the QR service allows
// CORS) so the browser reliably saves it, then falls back to opening the image
// URL in a new tab if the fetch is blocked.
async function downloadQRPng(fullUrl: string, fileBase: string) {
  const imgUrl = getQRImageUrl(fullUrl, 600); // larger, print-friendly PNG
  const fileName = `${fileBase}-qr.png`;
  try {
    const res = await fetch(imgUrl);
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch {
    window.open(imgUrl, '_blank');
  }
}

function slugifyLabel(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'qr-code';
}

/* ─── Component ─── */

export function QRCodeDirectoryPage() {
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const baseUrl = window.location.origin;

  const filtered = QR_CODES.filter((entry) => {
    if (filterCategory !== 'all' && entry.category !== filterCategory) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        entry.label.toLowerCase().includes(term) ||
        entry.description.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const handlePrint = () => {
    const rows = filtered
      .map(
        (entry) => `
        <tr>
          <td style="text-align:center;padding:12px;">
            <img src="${getQRImageUrl(baseUrl + entry.path, 150)}" width="150" height="150" alt="${entry.label}" />
          </td>
          <td style="padding:12px;vertical-align:top;">
            <strong style="font-size:14px;">${entry.icon} ${entry.label}</strong><br/>
            <span style="font-size:11px;color:#666;">${baseUrl}${entry.path}</span><br/>
            <span style="font-size:12px;color:#333;margin-top:4px;display:inline-block;">${entry.description}</span>
          </td>
        </tr>`
      )
      .join('');

    const html = `<!DOCTYPE html><html><head>
      <title>QR Code Directory — KDU Georgia 2026</title>
      <style>
        body { font-family: sans-serif; padding: 20px; }
        table { width: 100%; border-collapse: collapse; }
        tr { border-bottom: 1px solid #ddd; }
        td { padding: 12px; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        p { color: #666; font-size: 12px; margin-bottom: 16px; }
      </style>
    </head><body>
      <h1>QR Code Directory — KDU Georgia 2026</h1>
      <p>${filtered.length} codes • Printed ${new Date().toLocaleDateString()}</p>
      <table><tbody>${rows}</tbody></table>
    </body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.heading}>QR Code Directory</h1>
          <p style={styles.subtitle}>
            Scannable codes for all key app pages. Print and place at signage stations, registration desks, and event venues.
          </p>
        </div>
        <button style={styles.printBtn} onClick={handlePrint} aria-label="Print QR codes">
          🖨️ Print
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        <div style={styles.filterTabs}>
          <button
            style={filterCategory === 'all' ? styles.filterTabActive : styles.filterTab}
            onClick={() => setFilterCategory('all')}
          >
            All ({QR_CODES.length})
          </button>
          {CATEGORIES.map((cat) => {
            const count = QR_CODES.filter((e) => e.category === cat.key).length;
            return (
              <button
                key={cat.key}
                style={filterCategory === cat.key ? styles.filterTabActive : styles.filterTab}
                onClick={() => setFilterCategory(cat.key)}
              >
                {cat.icon} {cat.label} ({count})
              </button>
            );
          })}
        </div>
        <input
          type="text"
          placeholder="Search codes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
          aria-label="Search QR codes"
        />
      </div>

      {/* Grid */}
      <div style={styles.grid}>
        {filtered.map((entry) => {
          const fullUrl = baseUrl + entry.path;
          return (
            <div key={entry.id} style={styles.card}>
              <div style={styles.qrWrapper}>
                <img
                  src={getQRImageUrl(fullUrl)}
                  alt={`QR code for ${entry.label}`}
                  width={160}
                  height={160}
                  style={styles.qrImage}
                  loading="lazy"
                />
              </div>
              <div style={styles.cardContent}>
                <h3 style={styles.cardTitle}>
                  <span style={styles.cardIcon}>{entry.icon}</span> {entry.label}
                </h3>
                <p style={styles.cardDescription}>{entry.description}</p>
                <code style={styles.cardUrl}>{entry.path}</code>
                <div style={styles.cardActions}>
                  <button
                    style={styles.downloadBtn}
                    onClick={() => downloadQRPng(fullUrl, slugifyLabel(entry.label))}
                    aria-label={`Download QR code PNG for ${entry.label}`}
                    title="Download PNG"
                  >
                    ⬇ Download PNG
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={styles.empty}>No QR codes match your search.</div>
      )}
    </div>
  );
}

/* ─── Styles ─── */

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1.5rem',
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: TEXT_MUTED,
    margin: 0,
    maxWidth: '600px',
  },
  printBtn: {
    padding: '0.6rem 1.2rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  filterBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  filterTabs: {
    display: 'flex',
    gap: '0.4rem',
    flexWrap: 'wrap',
  },
  filterTab: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#e5e7eb',
    color: TEXT_DARK,
    border: 'none',
    borderRadius: '16px',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  filterTabActive: {
    padding: '0.4rem 0.75rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '16px',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  searchInput: {
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.85rem',
    width: '200px',
    outline: 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1.25rem',
  },
  card: {
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    border: `1px solid ${BORDER_LIGHT}`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1.25rem',
    gap: '0.75rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  qrWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0.5rem',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  qrImage: {
    borderRadius: '4px',
  },
  cardContent: {
    textAlign: 'center',
    width: '100%',
  },
  cardTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.4rem 0',
  },
  cardIcon: {
    marginRight: '0.25rem',
  },
  cardDescription: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    margin: '0 0 0.5rem 0',
    lineHeight: 1.4,
  },
  cardUrl: {
    fontSize: '0.72rem',
    color: PRIMARY,
    backgroundColor: '#f0fdf4',
    padding: '0.2rem 0.5rem',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  cardActions: {
    marginTop: '0.75rem',
    display: 'flex',
    justifyContent: 'center',
  },
  downloadBtn: {
    padding: '0.45rem 0.9rem',
    backgroundColor: '#fff',
    color: PRIMARY,
    border: `1.5px solid ${PRIMARY}`,
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.95rem',
  },
};
